import { Vec3, sub, dot, cross, len, normalize, closestPointOnSegment, EPS } from './vec';
import { FaceData, Model, faceUsesEdge } from './model';
import { faceBasis, faceNormal, pointInPolygon, toBasis2D, triangulateFace, PlaneBasis } from './face';
import { findOrCreateVertex } from './ops';

/**
 * Linien wie SketchUp: Endpunkte auf bestehenden Kanten teilen diese, und sobald
 * eine Linie einen ebenen, geschlossenen Kantenring bildet, entsteht automatisch
 * eine Fläche. Eine Linie quer über eine Fläche (Rand zu Rand) teilt die Fläche.
 */

const PLANE_TOL = 1e-4;

export interface LineResult {
  edgeId: number | null;
  /** Neu entstandene Flächen (auch Teilflächen). */
  faceIds: number[];
}

/** Legt eine Linie a→b an und bildet/teilt Flächen wie SketchUp. */
export function addLineSegment(model: Model, a: Vec3, b: Vec3): LineResult {
  const va = findOrCreateVertex(model, a);
  splitEdgesAtVertex(model, va);
  const vb = findOrCreateVertex(model, b);
  splitEdgesAtVertex(model, vb);
  if (va === vb) return { edgeId: null, faceIds: [] };

  // Schnittpunkte mit vorhandenen Kanten des Kontexts: beide Linien dort teilen (wie SketchUp)
  const pa = model.vertexPosition(va);
  const pb = model.vertexPosition(vb);
  for (const e of [...model.edges.values()]) {
    if (e.group !== model.editContext || e.a === va || e.a === vb || e.b === va || e.b === vb) continue;
    const [ea, eb] = model.edgePoints(e);
    const x = segmentIntersection(pa, pb, ea, eb);
    if (!x) continue;
    const vx = findOrCreateVertex(model, x);
    splitEdgesAtVertex(model, vx);
  }

  // Bestehende Eckpunkte auf der Strecke teilen die Linie (überlappende Kanten verschmelzen)
  const ctx = model.contextVertexIds();
  const inner: { id: number; t: number }[] = [];
  for (const id of ctx) {
    if (id === va || id === vb) continue;
    const hit = closestPointOnSegment(model.vertexPosition(id), pa, pb);
    if (hit.distance < PLANE_TOL && hit.t > 1e-6 && hit.t < 1 - 1e-6) inner.push({ id, t: hit.t });
  }
  inner.sort((x, y) => x.t - y.t);
  const chain = [va, ...inner.map((x) => x.id), vb];

  const faceIds: number[] = [];
  let lastEdge: number | null = null;
  for (let i = 0; i + 1 < chain.length; i++) {
    const edge = model.addEdge(chain[i]!, chain[i + 1]!);
    if (!edge) continue;
    lastEdge = edge.id;
    // Auch eine nachgezogene Kante kann einen Ring schließen (SketchUp: Fläche „heilen“)
    faceIds.push(...formFacesForEdge(model, edge.id));
  }
  return { edgeId: lastEdge, faceIds };
}

/** Liegt der Vertex im Inneren einer Kante des Kontexts, wird die Kante dort geteilt (Flächenringe inklusive). */
export function splitEdgesAtVertex(model: Model, vid: number): void {
  const p = model.vertexPosition(vid);
  for (const e of [...model.edges.values()]) {
    if (e.group !== model.editContext || e.a === vid || e.b === vid) continue;
    const [pa, pb] = model.edgePoints(e);
    const hit = closestPointOnSegment(p, pa, pb);
    if (hit.distance > PLANE_TOL || hit.t <= 1e-6 || hit.t >= 1 - 1e-6) continue;
    // Vertex in alle Ringe einfügen, die die Kante a–b enthalten
    for (const f of model.faces.values()) {
      if (!faceUsesEdge(f, e.a, e.b)) continue;
      f.outer = insertBetween(f.outer, e.a, e.b, vid);
      f.holes = f.holes.map((h) => insertBetween(h, e.a, e.b, vid));
    }
    const oldB = e.b;
    e.b = vid;
    model.addEdge(vid, oldB);
    return;
  }
}

function insertBetween(loop: number[], a: number, b: number, v: number): number[] {
  for (let i = 0; i < loop.length; i++) {
    const x = loop[i]!, y = loop[(i + 1) % loop.length]!;
    if ((x === a && y === b) || (x === b && y === a)) {
      const out = [...loop];
      out.splice(i + 1, 0, v);
      return out;
    }
  }
  return loop;
}

/** Nach dem Anlegen einer Kante: Fläche teilen oder neue Flächen aus geschlossenen Ringen bilden. */
export function formFacesForEdge(model: Model, edgeId: number): number[] {
  const edge = model.edges.get(edgeId);
  if (!edge) return [];
  const split = splitFaceAlongChain(model, edge.a, edge.b, edgeId);
  if (split !== null) return [split];

  const created: number[] = [];
  const pa = model.vertexPosition(edge.a);
  const pb = model.vertexPosition(edge.b);
  const ab = normalize(sub(pb, pa));
  for (const plane of candidatePlanes(model, edge.a, edge.b, ab)) {
    // Bis zu zwei Ringe je Ebene (Linie teilt einen flächenlosen Ring in zwei Seiten)
    const blocked = new Set<number>();
    for (let k = 0; k < 2; k++) {
      const path = shortestPath(model, edge.b, edge.a, edgeId, (v) => onPlane(model, v, plane), blocked);
      if (!path || path.length < 3) break;
      path.slice(1, -1).forEach((v) => blocked.add(v));
      const fid = tryCreateFace(model, path);
      if (fid !== null) created.push(fid);
    }
  }
  return created;
}

interface Plane { point: Vec3; normal: Vec3 }

function onPlane(model: Model, v: number, plane: Plane): boolean {
  return Math.abs(dot(sub(model.vertexPosition(v), plane.point), plane.normal)) < PLANE_TOL;
}

/** Ebenen durch die neue Kante und je einen benachbarten Kantenendpunkt. */
function candidatePlanes(model: Model, a: number, b: number, ab: Vec3): Plane[] {
  const pa = model.vertexPosition(a);
  const planes: Plane[] = [];
  for (const e of model.edges.values()) {
    if (e.group !== model.editContext) continue;
    let other: number | null = null;
    if (e.a === a || e.a === b) other = e.b;
    else if (e.b === a || e.b === b) other = e.a;
    if (other === null || other === a || other === b) continue;
    const n = cross(ab, sub(model.vertexPosition(other), pa));
    if (len(n) < 1e-9) continue;
    const normal = normalize(n);
    if (planes.some((p) => Math.abs(Math.abs(dot(p.normal, normal)) - 1) < 1e-6 && Math.abs(dot(sub(pa, p.point), p.normal)) < PLANE_TOL)) continue;
    planes.push({ point: pa, normal });
  }
  return planes;
}

/** Kürzester Kantenweg (BFS) von `from` nach `to` über lose Kanten des Kontexts. */
function shortestPath(
  model: Model,
  from: number,
  to: number,
  skipEdge: number,
  allowed: (v: number) => boolean,
  blocked: Set<number>
): number[] | null {
  const adj = new Map<number, number[]>();
  for (const e of model.edges.values()) {
    if (e.id === skipEdge || e.group !== model.editContext) continue;
    if (!allowed(e.a) || !allowed(e.b)) continue;
    (adj.get(e.a) ?? adj.set(e.a, []).get(e.a)!).push(e.b);
    (adj.get(e.b) ?? adj.set(e.b, []).get(e.b)!).push(e.a);
  }
  const prev = new Map<number, number>([[from, from]]);
  const queue = [from];
  while (queue.length > 0) {
    const v = queue.shift()!;
    if (v === to) break;
    for (const n of adj.get(v) ?? []) {
      if (prev.has(n) || (blocked.has(n) && n !== to)) continue;
      prev.set(n, v);
      queue.push(n);
    }
  }
  if (!prev.has(to)) return null;
  const path = [to];
  let cur = to;
  while (cur !== from) {
    cur = prev.get(cur)!;
    path.push(cur);
  }
  return path.reverse();
}

/**
 * Legt aus einem ebenen Ring eine Fläche an, wenn dort noch keine liegt.
 * Liegt der Ring vollständig im Inneren einer bestehenden Fläche, wird er deren Loch.
 */
function tryCreateFace(model: Model, ring: number[]): number | null {
  if (new Set(ring).size !== ring.length || ring.length < 3) return null;
  const pts = ring.map((v) => model.vertexPosition(v));
  if (!isSimplePolygon(pts)) return null;
  const tri = triangulateFace(pts, []).triangles;
  if (tri.length < 3) return null;
  const inner = {
    x: (tri[0]!.x + tri[1]!.x + tri[2]!.x) / 3,
    y: (tri[0]!.y + tri[1]!.y + tri[2]!.y) / 3,
    z: (tri[0]!.z + tri[1]!.z + tri[2]!.z) / 3
  };
  const n = faceNormal(pts);
  let host: FaceData | null = null;
  for (const f of model.faces.values()) {
    if (f.group !== model.editContext) continue;
    const fp = f.outer.map((v) => model.vertexPosition(v));
    const fn = faceNormal(fp);
    if (Math.abs(Math.abs(dot(fn, n)) - 1) > 1e-6 || Math.abs(dot(sub(inner, fp[0]!), fn)) > PLANE_TOL) continue;
    const basis = faceBasis(fp);
    if (!insideFace(model, f, basis, inner)) continue;
    // Deckungsgleiche Fläche existiert schon, oder Ring berührt den Rand → nichts tun
    const outerSet = new Set(f.outer);
    if (ring.some((v) => outerSet.has(v))) return null;
    // Ring liegt nicht ganz in f → f ist keine Gastfläche (evtl. vom Ring umschlossen)
    if (!pts.every((p) => insideFace(model, f, basis, p))) continue;
    host = f;
    break;
  }
  // Vorhandene Flächen, die vollständig im neuen Ring liegen, werden dessen Löcher
  // (sonst lägen zwei Flächen übereinander). Verschachtelte zählen nur einmal (die äußerste).
  const ringBasis = faceBasis(pts);
  const ring2 = pts.map((p) => toBasis2D(ringBasis, p));
  const ringSet = new Set(ring);
  const enclosed: FaceData[] = [];
  for (const f of model.faces.values()) {
    if (f.group !== model.editContext || f === host) continue;
    const fp = f.outer.map((v) => model.vertexPosition(v));
    if (fp.some((p) => Math.abs(dot(sub(p, pts[0]!), n)) > PLANE_TOL)) continue;
    if (f.outer.some((v) => ringSet.has(v))) continue;
    if (fp.every((p) => { const q = toBasis2D(ringBasis, p); return pointInPolygon(q.x, q.y, ring2); })) enclosed.push(f);
  }
  const outermost = enclosed.filter((f) => !enclosed.some((g) => g !== f && faceInside(model, f, g)));
  const holes = outermost.map((f) => [...f.outer]);

  const face = model.addFace(ring, holes);
  if (!face) return null;
  if (host) {
    face.color = host.color;
    // Löcher der Gastfläche, die jetzt im neuen Ring liegen, gehören nicht mehr ihr
    const holeKeys = new Set(holes.map((h) => [...h].sort((x, y) => x - y).join(',')));
    host.holes = host.holes.filter((h) => !holeKeys.has([...h].sort((x, y) => x - y).join(',')));
    host.holes.push([...ring]);
  }
  return face.id;
}

/** Liegt Fläche `inner` vollständig in Fläche `outer` (gleiche Ebene angenommen)? */
function faceInside(model: Model, inner: FaceData, outer: FaceData): boolean {
  const op = outer.outer.map((v) => model.vertexPosition(v));
  const b = faceBasis(op);
  const o2 = op.map((p) => toBasis2D(b, p));
  return inner.outer.every((v) => {
    const q = toBasis2D(b, model.vertexPosition(v));
    return pointInPolygon(q.x, q.y, o2);
  });
}

/**
 * Fläche aus einem gezeichneten Ring (Punkte) bilden, falls dort noch keine liegt —
 * Rückfall für Formwerkzeuge, wenn die Kantensuche einen anderen Ring gefunden hat.
 */
export function faceFromRing(model: Model, points: Vec3[]): number | null {
  const ids: number[] = [];
  for (const p of points) {
    let id: number | null = null;
    for (const v of model.contextVertexIds()) {
      const q = model.vertexPosition(v);
      if (Math.hypot(q.x - p.x, q.y - p.y, q.z - p.z) < PLANE_TOL) { id = v; break; }
    }
    if (id === null) return null;
    ids.push(id);
  }
  // Ring bereits als Fläche vorhanden?
  const key = [...ids].sort((a, b) => a - b).join(',');
  for (const f of model.faces.values()) {
    if (f.group === model.editContext && [...f.outer].sort((a, b) => a - b).join(',') === key) return null;
  }
  return tryCreateFace(model, ids);
}

/** Schnittpunkt zweier Strecken im Raum (beide im Inneren, windschief → null). */
export function segmentIntersection(p1: Vec3, p2: Vec3, q1: Vec3, q2: Vec3): Vec3 | null {
  const d1 = sub(p2, p1), d2 = sub(q2, q1), r = sub(p1, q1);
  const a = dot(d1, d1), e = dot(d2, d2), f = dot(d2, r);
  const b = dot(d1, d2), c = dot(d1, r);
  const denom = a * e - b * b;
  if (a < 1e-12 || e < 1e-12 || denom < 1e-12 * a * e) return null; // parallel/entartet
  const s = (b * f - c * e) / denom;
  const t = (a * f - b * c) / denom;
  const eps = 1e-6;
  if (s <= eps || s >= 1 - eps || t <= eps || t >= 1 - eps) return null;
  const x1 = { x: p1.x + d1.x * s, y: p1.y + d1.y * s, z: p1.z + d1.z * s };
  const x2 = { x: q1.x + d2.x * t, y: q1.y + d2.y * t, z: q1.z + d2.z * t };
  if (Math.hypot(x1.x - x2.x, x1.y - x2.y, x1.z - x2.z) > PLANE_TOL) return null;
  return x1;
}

function insideFace(model: Model, f: FaceData, basis: PlaneBasis, p: Vec3): boolean {
  const q = toBasis2D(basis, p);
  const outer = f.outer.map((v) => toBasis2D(basis, model.vertexPosition(v)));
  if (!pointInPolygon(q.x, q.y, outer)) return false;
  return !f.holes.some((h) => pointInPolygon(q.x, q.y, h.map((v) => toBasis2D(basis, model.vertexPosition(v)))));
}

/** Nicht selbstschneidend, nicht entartet (2D in der Ringebene). */
function isSimplePolygon(pts: Vec3[]): boolean {
  const basis = faceBasis(pts);
  if (len(basis.n) < 0.5) return false;
  const p2 = pts.map((p) => toBasis2D(basis, p));
  let area = 0;
  for (let i = 0; i < p2.length; i++) {
    const a = p2[i]!, b = p2[(i + 1) % p2.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  if (Math.abs(area) < 1e-8) return false;
  const n = p2.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (segmentsCross(p2[i]!, p2[(i + 1) % n]!, p2[j]!, p2[(j + 1) % n]!)) return false;
    }
  }
  return true;
}

function segmentsCross(a: { x: number; y: number }, b: { x: number; y: number }, c: { x: number; y: number }, d: { x: number; y: number }): boolean {
  const o = (p: { x: number; y: number }, q: { x: number; y: number }, r: { x: number; y: number }) =>
    (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
  const d1 = o(c, d, a), d2 = o(c, d, b), d3 = o(a, b, c), d4 = o(a, b, d);
  return ((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS)) && ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS));
}

/**
 * Teilt eine Fläche, wenn die neue Kante Teil eines Kantenzugs ist, der zwei
 * verschiedene Randpunkte der Fläche durch ihr Inneres verbindet. Liefert die neue Teilfläche.
 */
function splitFaceAlongChain(model: Model, a: number, b: number, edgeId: number): number | null {
  const pa = model.vertexPosition(a);
  const pb = model.vertexPosition(b);
  const mid = { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2, z: (pa.z + pb.z) / 2 };
  for (const f of [...model.faces.values()]) {
    if (f.group !== model.editContext) continue;
    const fp = f.outer.map((v) => model.vertexPosition(v));
    const basis = faceBasis(fp);
    const plane = { point: fp[0]!, normal: basis.n };
    if (!onPlane(model, a, plane) || !onPlane(model, b, plane)) continue;
    if (!insideFace(model, f, basis, mid)) continue;
    // Kante liegt auf dem Rand? Dann ist nichts zu teilen
    if (faceUsesEdge(f, a, b)) continue;

    const ring = new Set(f.outer);
    const inFace = (v: number) => ring.has(v) || (onPlane(model, v, plane) && insideFace(model, f, basis, model.vertexPosition(v)));
    // Von beiden Enden über Kanten im Flächeninneren zum Rand laufen
    const toRing = (start: number, avoid: number): number[] | null => {
      if (ring.has(start)) return [start];
      const blocked = new Set<number>([avoid]);
      let best: number[] | null = null;
      for (const r of ring) {
        const path = shortestPath(model, start, r, edgeId, (v) => inFace(v) && (!ring.has(v) || v === r), blocked);
        if (path && (!best || path.length < best.length)) best = path;
      }
      return best;
    };
    const pathA = toRing(a, b);
    const pathB = toRing(b, a);
    if (!pathA || !pathB) continue;
    const r1 = pathA[pathA.length - 1]!;
    const r2 = pathB[pathB.length - 1]!;
    if (r1 === r2) continue;
    const chain = [...pathA].reverse().concat(pathB); // r1 … a, b … r2
    if (new Set(chain).size !== chain.length) continue;
    const interior = chain.slice(1, -1);

    const i1 = f.outer.indexOf(r1), i2 = f.outer.indexOf(r2);
    const walk = (from: number, to: number): number[] => {
      const out: number[] = [];
      for (let i = from; ; i = (i + 1) % f.outer.length) {
        out.push(f.outer[i]!);
        if (i === to) break;
      }
      return out;
    };
    const ringA = walk(i1, i2).concat([...interior].reverse()); // r1 → r2 am Rand, zurück über die Kette
    const ringB = walk(i2, i1).concat(interior); // r2 → r1 am Rand, weiter über die Kette
    const holes = f.holes;
    const holeInRing = (h: number[], r: number[]) => {
      const q = toBasis2D(basis, model.vertexPosition(h[0]!));
      return pointInPolygon(q.x, q.y, r.map((v) => toBasis2D(basis, model.vertexPosition(v))));
    };
    f.outer = ringA;
    f.holes = holes.filter((h) => holeInRing(h, ringA));
    const nf = model.addFace(ringB, holes.filter((h) => !holeInRing(h, ringA)), undefined, f.color);
    return nf ? nf.id : null;
  }
  return null;
}
